<template>
    <div ref="searchWrapper" class="relative">
        <Commerce-SearchInput v-model="query" :placeholder="placeholder" @update:modelValue="handleSearch" />

        <div v-if="showResults && services.length"
            class="absolute z-[999] mt-2 w-full overflow-hidden rounded-xl border border-border bg-card shadow-lg">
            <div class="relative">
                <div ref="scrollContainer" @scroll="updateThumb"
                    class="max-h-60 overflow-y-auto divide-y divide-border no-native-scrollbar">
                    <NuxtLink v-for="service in services" :key="service.id" :to="`/${service.slug}`"
                        class="flex items-center justify-between gap-4 p-4 transition-colors hover:bg-secondary/60">
                        <div class="min-w-0">
                            <div class="truncate text-sm font-medium text-foreground">
                                {{ service.name }}
                            </div>
                            <div v-if="service.provider?.name" class="mt-1 truncate text-xs text-muted-foreground">
                                {{ service.provider.name }}
                            </div>
                        </div>
                        <div v-if="service.start_from" class="shrink-0 text-xs">
                            <span class="text-muted-foreground">شروع از </span>
                            <span class="font-bold text-accent nums">
                                {{ service.start_from ? fx(Number(service.start_from), 'USD') : '-' }}
                            </span>
                        </div>
                    </NuxtLink>
                </div>

                <div v-if="hasScroll" class="custom-thumb"
                    :style="{ height: thumbHeight + 'px', transform: `translateY(${thumbTop}px)` }" />
            </div>
        </div>

        <div v-if="showResults && !loadingServices && !services.length && query.trim()"
            class="absolute z-[999] mt-2 w-full rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground shadow-lg">
            داده‌ای یافت نشد
        </div>

        <div v-if="loadingServices && showResults"
            class="absolute z-[9999] mt-2 w-full rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground shadow-lg">
            در حال جستجو...
        </div>
    </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import { nextTick, ref } from 'vue'
import { fx } from '~/utils/format'

const props = defineProps<{
  placeholder?: string
}>()

const query = ref('')
const showResults = ref(false)
const searchWrapper = ref<HTMLElement | null>(null)
const scrollContainer = ref<HTMLElement | null>(null)
const thumbHeight = ref(0)
const thumbTop = ref(0)
const hasScroll = ref(false)

const { services, loadingServices, setFilter, clearFilters } = useServices()

let timer: ReturnType<typeof setTimeout>

function updateThumb() {
  const el = scrollContainer.value
  if (!el) return

  const { scrollTop, scrollHeight, clientHeight } = el

  if (scrollHeight <= clientHeight) {
    hasScroll.value = false
    return
  }

  hasScroll.value = true
  thumbHeight.value = Math.max((clientHeight / scrollHeight) * clientHeight, 20)
  thumbTop.value = (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - thumbHeight.value)
}

const handleSearch = (value: string) => {
  query.value = value
  clearTimeout(timer)

  if (!value.trim()) {
    showResults.value = false
    clearFilters()
    return
  }

  showResults.value = true

  timer = setTimeout(async () => {
    setFilter('name', value.trim())

    await nextTick()
    updateThumb()
  }, 800)
}

onClickOutside(searchWrapper, () => {
  showResults.value = false
})
</script>

<style scoped>
.no-native-scrollbar {
    -ms-overflow-style: none !important;
    scrollbar-width: none !important;
}

.no-native-scrollbar::-webkit-scrollbar {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
}

.custom-thumb {
    position: absolute;
    top: 0;
    inset-inline-end: 3px;
    width: 4px;
    border-radius: 999px;
    background-color: rgba(156, 163, 175, 0.6);
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s ease;
}

.relative:hover .custom-thumb {
    opacity: 1;
}
</style>